import React, { useEffect, useState } from "react";
import { Copy, Landmark, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectCollectionAccount,
  selectMandateForMember,
} from "../../features/collection/collection-slice";
import {
  fetchCollectionAccount,
  fetchMandates,
  requestMandate,
} from "../../features/collection/slice-extensions/collection-slice-extension";
import { CollectionAccountStatus } from "../../features/collection/models/CollectionAccountStatus";
import { DebitMandateStatus } from "../../features/collection/models/DebitMandateStatus";
import { MandateRequestResponse } from "../../features/collection/contracts/MandateRequestResponse";

/**
 * The member's automatic-payment standing on their billing section (SEPA mandate or card
 * on file, PD-0043): the mandate's label and status ("Visa ···4242 · active"), plus the
 * "Ask for a payment authorization" action that emails the member a secure setup link
 * (also copyable for hand-sharing). Renders nothing until the organization's collection
 * account is ready.
 */
const MemberDirectDebitSection: React.FC<{ memberId: string }> = ({ memberId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectCollectionAccount);
  const mandate = useAppSelector(selectMandateForMember(memberId));
  const [request, setRequest] = useState<MandateRequestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dispatch(fetchCollectionAccount());
    dispatch(fetchMandates());
  }, [dispatch]);

  if (account?.status !== CollectionAccountStatus.Active || account.methods.length === 0) {
    return null;
  }

  const handleRequest = async () => {
    setError(null);
    setSending(true);
    try {
      const response = await dispatch(requestMandate(memberId)).unwrap();
      setRequest(response);
      dispatch(fetchMandates());
    } catch (e) {
      setError((e as { message?: string })?.message ?? t("collection.mandate.requestFailed"));
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    if (!request) return;
    await navigator.clipboard.writeText(request.setupUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const badge = mandate?.status === DebitMandateStatus.Active
    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
    : "bg-grey-95 text-grey-30 dark:bg-primary-20 dark:text-grey-70";

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-grey-90 px-3 py-2 dark:border-primary-20">
      <div className="flex items-center gap-2 text-sm">
        <Landmark size={16} className="shrink-0 text-grey-30 dark:text-grey-70" />
        <span className="font-medium text-grey-10 dark:text-grey-90">
          {t("collection.mandate.title")}
        </span>
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${badge}`}>
          {mandate?.status === DebitMandateStatus.Active && mandate.label
            ? t("collection.mandate.activeLabel", { label: mandate.label })
            : mandate
              ? t(`collection.mandateStatus.${mandate.status}`, { defaultValue: mandate.status })
              : t("collection.mandate.none")}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {error && (
          <span className="text-xs font-medium text-red-700 dark:text-red-400">{error}</span>
        )}
        {request && (
          <>
            <span className="text-xs text-grey-40 dark:text-grey-60">
              {request.emailSent
                ? t("collection.mandate.linkSent")
                : t("collection.mandate.linkNotSent")}
            </span>
            <button type="button" className="btn btn-text" onClick={handleCopy}>
              <Copy size={14} />
              <span>{copied ? t("collection.mandate.copied") : t("collection.mandate.copyLink")}</span>
            </button>
          </>
        )}
        {mandate?.status !== DebitMandateStatus.Active && !request && (
          <button type="button" className="btn btn-secondary" disabled={sending} onClick={handleRequest}>
            <Send size={16} />
            <span>{t("collection.mandate.request")}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default MemberDirectDebitSection;
